import { $fetch } from '@/boot/fetch'
import Addresses from './retailers/addresses'
import OrderLookup from './retailers/orderLookup'
import Orders from './retailers/orders'
import PickupLocations from './retailers/pickupLocations'
import Reports from './retailers/reports'
import Insights from './retailers/insights'
import Settings from './retailers/settings'
import ProductTags from './retailers/productTags' // PEG-967
import ShippingPartners from './retailers/shippingPartners'
import Vendors from './retailers/vendors'
import Claims from './retailers/claims' // PEG-546
import TourStatus from './retailers/tourStatus' // PEG-696
import { replacePathTokens } from '@/utils/routes.util'

const ENDPOINT = '/retailers/{{retailerId}}'

export default {
  Addresses,
  OrderLookup,
  Orders,
  PickupLocations,
  Insights,
  Reports,
  Settings,
  ProductTags, // PEG-967
  ShippingPartners,
  Vendors,
  Claims, // PEG-546
  TourStatus, // PEG-696
  /**
   * @description Get a retailer by ID
   * @function getSingle
   * @param {String} retailerId - The retailer ID
   * @returns {Promise}
   */
  async getSingle(retailerId) {
    return $fetch.peg(replacePathTokens(ENDPOINT, { retailerId }))
  },

  /**
   * @description Update a single retailer
   * @function update
   * @param {String} retailerId - The retailer ID
   * @param {Object} body - The updated retailer object
   * @returns {Promise}
   */
  async updateSingle(retailerId, body) {
    return $fetch.peg(replacePathTokens(ENDPOINT, { retailerId }), {
      method: 'PUT',
      body,
    })
  },

  /**
   * @description Update returner eligibility status (block/unblock)
   * @function updateReturnerEligibility
   * @param {String} retailerId - The retailer ID
   * @param {String} userId - User ID
   * @param {Object} body - The updated returner object
   * @returns {Promise}
   */
  async updateReturnerEligibility(retailerId, userId, body = {}) {
    body = { allowReturns: true, ...body }
    const endpoint = `${ENDPOINT}/returners/{{userId}}`

    return $fetch.peg(replacePathTokens(endpoint, { retailerId, userId }), {
      method: 'PUT',
      body,
    })
  },

  /**
   * @description Lookup a product by barcode for a specific retailer
   * @function productLookup
   * @param {String} retailerId - The retailer ID
   * @param {String} barcode - The product barcode
   * @returns {Promise}
   */
  async productLookup(retailerId, barcode) {
    const endpoint = `${ENDPOINT}/productLookup`

    return $fetch.peg(replacePathTokens(endpoint, { retailerId }), {
      method: 'POST',
      body: { barcode },
    })
  },

  /**
   * @description Get public retailer data (for customer-facing views)
   * @function getPublic
   * @param {String} retailerId - The retailer ID
   * @returns {Promise}
   */
  async getPublic(retailerId) {
    const endpoint = `${ENDPOINT}/public`

    return $fetch.peg(replacePathTokens(endpoint, { retailerId }))
  },

  /**
   * @description Check Shopify store configuration (customer accounts version, store credit)
   * @function checkShopifyConfig
   * @param {String} retailerId - The retailer ID
   * @returns {Promise}
   */
  async checkShopifyConfig(retailerId) {
    const endpoint = `${ENDPOINT}/shopifyConfig`
    return $fetch.peg(replacePathTokens(endpoint, { retailerId }))
  },

  /**
   * @description Fingerprint a candidate URL to determine if it points at an
   * active Shopify storefront (PEG-781). The endpoint takes the URL as a query
   * parameter and never mutates persisted state — used during onboarding to
   * gate the chat flow on a valid Shopify URL.
   * @function fingerprintShopifyUrl
   * @param {String} retailerId - The retailer ID
   * @param {String} url - The candidate storefront URL to probe
   * @returns {Promise<{isShopify?:boolean,normalizedUrl?:string,finalUrl?:string,checks?:object,error?:string,message?:string}>}
   *   $fetch.peg flattens the 2xx envelope, so callers receive
   *   isShopify/checks at the top level. Throws on non-2xx — callers must
   *   use try/catch (per AGENTS.md API Layer).
   */
  async fingerprintShopifyUrl(retailerId, url) {
    const endpoint = `${ENDPOINT}/shopify/fingerprint`
    return $fetch.peg(replacePathTokens(endpoint, { retailerId }), {
      params: { url },
    })
  },

  /**
   * @description Enqueue the URL-driven storefront + return-policy scrape
   * (PEG-749/782). Fire-and-forget: the backend returns 202 and a peg-tasks
   * worker performs the scrape + AI extraction asynchronously. Poll
   * onboardingPolicyForm.get() (latestScrape) for completion.
   * @function enqueuePolicyScrape
   * @param {String} retailerId - The retailer ID
   * @param {String} sourceUrl - Any URL on the retailer's storefront
   * @returns {Promise}
   */
  async enqueuePolicyScrape(retailerId, sourceUrl) {
    const endpoint = `${ENDPOINT}/policy-scrape`
    return $fetch.peg(replacePathTokens(endpoint, { retailerId }), {
      method: 'POST',
      body: { sourceUrl },
    })
  },
}
